export interface Deployment {
  id: string;
  name: string;
  convexUrl: string;
  clerkPublishableKey: string;
  environment: "development" | "staging" | "production";
  allowedDomains: string[];
}

const deployments: Deployment[] = [
  {
    id: "dev",
    name: "Dev",
    convexUrl: "https://keen-jackal-905.convex.cloud",
    clerkPublishableKey: "pk_test_ZW5hYmxlZC1xdWV0emFsLTMyLmNsZXJrLmFjY291bnRzLmRldiQ",
    environment: "development",
    allowedDomains: ["localhost", "lovableproject.com"],
  },
  {
    id: "production",
    name: "Prod",
    convexUrl: "https://hip-gazelle-676.convex.cloud",
    clerkPublishableKey: "pk_live_Y2xlcmsuc2hvbXJpLmNvbSQ",
    environment: "production",
    allowedDomains: ["shomri.com"],
  },
];

const isAllowedOnCurrentDomain = (deployment: Deployment): boolean => {
  if (typeof window === "undefined") return true;
  const currentDomain = window.location.hostname;
  return deployment.allowedDomains.some(
    (domain) => currentDomain === domain || currentDomain.endsWith(`.${domain}`)
  );
};

const DEFAULT_DEPLOYMENT_ID = "dev";
const STORAGE_KEY = "convex-deployment";

export const getAvailableDeployments = (): Deployment[] => {
  return deployments.filter(isAllowedOnCurrentDomain);
};

export const getSelectedDeploymentId = (): string => {
  if (typeof window === "undefined") return DEFAULT_DEPLOYMENT_ID;
  const storedId = localStorage.getItem(STORAGE_KEY) || DEFAULT_DEPLOYMENT_ID;
  
  // Auto-fallback: if stored deployment isn't allowed on current domain, use first allowed
  const storedDeployment = deployments.find((d) => d.id === storedId);
  if (storedDeployment && isAllowedOnCurrentDomain(storedDeployment)) {
    return storedId;
  }
  
  const allowedDeployments = deployments.filter(isAllowedOnCurrentDomain);
  const fallbackId = allowedDeployments[0]?.id || DEFAULT_DEPLOYMENT_ID;
  localStorage.setItem(STORAGE_KEY, fallbackId);
  return fallbackId;
};

export const getSelectedDeployment = (): Deployment => {
  const selectedId = getSelectedDeploymentId();
  return deployments.find((d) => d.id === selectedId) || deployments.filter(isAllowedOnCurrentDomain)[0] || deployments[0];
};

export const setSelectedDeployment = (id: string): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, id);
};

export const getCurrentConvexUrl = (): string => {
  return getSelectedDeployment().convexUrl;
};

export const getCurrentClerkPublishableKey = (): string => {
  return getSelectedDeployment().clerkPublishableKey;
};
